<template>
  <b-table
    :items="mapped"
    :fields="fields"
    responsive
    hover
    head-variant="light"
    small
    show-empty
    :sort-by.sync="sortBy"
    :sort-desc.sync="sortDesc"
    :sort-compare="sortCompare"
  >
    <template #cell(amount)="d">
      {{ money(d.item.amount) }}
    </template>

    <template #cell(balance)="d">
      <span
        :class="{
          'text-success': d.item.paid_in_full,
          'text-danger': !d.item.paid_in_full && d.item.days_overdue >= 0,
          'text-body': !d.item.paid_in_full && d.item.days_overdue < 0
        }"
      >
        {{ money(d.item.balance) }}
      </span>
    </template>

    <template #cell(days_overdue)="d">
      <span :class="d.item.days_overdue >= 0 ? 'text-danger' : 'text-muted'">
        {{ d.item.days_overdue }}
      </span>
    </template>

    <!-- FOLIO como botón -->
    <template #cell(folio)="d">
      <b-button
        size="sm"
        variant="primary"
        class="font-weight-bolder"
        @click="goToQuote(d.item.quoteId)"
      >
        {{ d.item.folio }}
      </b-button>
    </template>

    <template #cell(remission)="d">
      <div class="d-flex align-items-center">
        <b-form-checkbox
          :checked="d.item.has_remission"
          disabled
          class="mb-0"
        />
        <small
          class="text-muted"
          :title="remissionTitle(d.item)"
        >
          {{ d.item.has_remission ? remissionLabel(d.item) : 'Pendiente' }}
        </small>
      </div>
    </template>

    <!-- Edición inline de factura -->
    <template #cell(invoice_number)="d">
      <div v-if="isEditing(d.item)">
        <b-form-input
          v-model.trim="invoiceDraft[d.item.poId]"
          size="sm"
          placeholder="Factura"
          class="w-75 d-inline-block mr-1"
        />
        <b-button
          size="sm"
          variant="primary"
          :disabled="!invoiceDraft[d.item.poId]"
          @click="save(d.item)"
        >
          Guardar
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          class="ml-1"
          @click="cancel(d.item)"
        >
          Cancelar
        </b-button>
      </div>

      <div v-else>
        <span v-if="d.item.invoice_number">{{ d.item.invoice_number }}</span>
        <span
          v-else
          class="text-muted"
        >—</span>
        <b-button
          size="sm"
          variant="link"
          class="p-0 ml-1"
          @click="start(d.item)"
        >
          {{ d.item.invoice_number ? 'Editar' : 'Agregar' }}
        </b-button>
      </div>
    </template>

    <!-- Fechas en dd-mm-aaaa -->
    <template #cell(sale_date)="d">
      {{ formatDate(d.item.sale_date) }}
    </template>
    <template #cell(due_date)="d">
      {{ formatDate(d.item.due_date) }}
    </template>

    <template #cell(actions)="d">
      <div
        v-if="!d.item.paid_in_full"
        class="d-flex align-items-center"
      >
        <currency-input
          v-model="payDraft[d.item.poId]"
          size="sm"
          min="0"
          :precision="2"
          :max="d.item.balance"
          class="mr-1"
          style="width: 110px;"
          placeholder="Monto"
          @input="validatePay(d.item)"
        />

        <b-button
          size="sm"
          variant="success"
          class="mr-1"
          :disabled="!!payError[d.item.poId] || !payDraft[d.item.poId]"
          @click="payPartial(d.item)"
        >
          Pagar
        </b-button>

        <b-button
          size="sm"
          variant="outline-success"
          class="mr-1"
          @click="$emit('pay-full', d.item.poId)"
        >
          Pagar todo
        </b-button>

        <small
          v-if="payError[d.item.poId]"
          class="text-danger"
        >
          {{ payError[d.item.poId] }}
        </small>
      </div>

      <b-button
        v-else
        size="sm"
        variant="outline-danger"
        @click="$emit('revert-payment', d.item.poId)"
      >
        Revertir pago
      </b-button>
    </template>
  </b-table>
</template>

<script>
import CurrencyInput from '@/components/CurrencyInput.vue'

export default {
  components: { CurrencyInput },
  props: { rows: { type: Array, default: () => [] } },
  data() {
    return {
      editing: {},
      invoiceDraft: {},
      // Orden inicial: folio ASC (el más antiguo arriba)
      sortBy: 'folio',
      sortDesc: false,

      payDraft: {},
      payError: {},
    }
  },
  computed: {
    fields() {
      return [
        { key: 'folio', label: 'Folio', sortable: true },
        { key: 'supplier', label: 'Proveedor' },
        { key: 'sale_date', label: 'Compra', sortable: true },
        { key: 'due_date', label: 'Vencimiento', sortable: true },
        { key: 'remission', label: 'Remisión', sortable: true },
        { key: 'invoice_number', label: 'Factura' },
        { key: 'amount', label: 'Importe', class: 'text-right' },
        { key: 'balance', label: 'Saldo', class: 'text-right' },
        { key: 'days_overdue', label: 'Días venc.', sortable: true },
        { key: 'actions', label: '', thStyle: { width: '190px' } },
      ]
    },
    mapped() {
      return (this.rows || []).map(r => ({
        poId: r.id,
        quoteId: r.quote_id,
        supplier: r && r.supplier ? r.supplier.name : null,
        sale_date: r.sale_date,
        due_date: r.due_date,
        folio: r.folio,
        invoice_number: r.invoice_number,
        has_remission: !!r.has_remission,
        remissions_count: Number(r.remissions_count || 0),
        remission: Number(r.remissions_count || 0),
        amount: r.amount,
        balance: r.balance,
        paid_in_full: r.paid_in_full,
        days_overdue: r.days_overdue,
      }))
    },
  },
  methods: {
    money(v) {
      const n = Number(v || 0)
      return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 })
    },

    formatDate(val) {
      if (!val) return '—'
      const d = new Date(val)
      if (Number.isNaN(d.getTime())) return '—'

      const dd = String(d.getUTCDate()).padStart(2, '0')
      const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
      const yyyy = d.getUTCFullYear()
      return `${dd}-${mm}-${yyyy}`
    },

    sortCompare(a, b, key) {
      const norm = v => (v === null || v === undefined ? '' : v)

      // Folio y días vencidos (numéricos)
      if (key === 'folio' || key === 'days_overdue' || key === 'remission') {
        const na = Number(norm(a[key]))
        const nb = Number(norm(b[key]))
        if (Number.isFinite(na) && Number.isFinite(nb)) {
          if (na === nb) return 0
          return na < nb ? -1 : 1
        }
      }

      // Fechas
      if (key === 'sale_date' || key === 'due_date') {
        const da = new Date(norm(a[key]))
        const db = new Date(norm(b[key]))
        const ta = da.getTime()
        const tb = db.getTime()
        if (!Number.isNaN(ta) && !Number.isNaN(tb)) {
          if (ta === tb) return 0
          return ta < tb ? -1 : 1
        }
      }

      // Texto genérico
      const sa = String(norm(a[key])).toLowerCase()
      const sb = String(norm(b[key])).toLowerCase()
      if (sa === sb) return 0
      return sa < sb ? -1 : 1
    },

    isEditing(item) { return !!this.editing[item.poId] },
    start(item) {
      this.$set(this.editing, item.poId, true)
      this.$set(this.invoiceDraft, item.poId, item.invoice_number || '')
    },
    cancel(item) {
      this.$delete(this.editing, item.poId)
      this.$delete(this.invoiceDraft, item.poId)
    },
    save(item) {
      this.$emit('save-invoice', {
        poId: item.poId,
        invoiceNumber: this.invoiceDraft[item.poId],
        done: () => this.cancel(item),
      })
    },
    goToQuote(quoteId) {
      if (!quoteId) return
      this.$router.push(`/orden-de-compra/${quoteId}`)
    },

    validatePay(item) {
      const id = item.poId
      const raw = this.payDraft[id]

      if (raw === null || raw === undefined || raw === '') {
        this.$delete(this.payError, id)
        return
      }

      const val = Number(raw)
      if (!Number.isFinite(val)) {
        this.$set(this.payError, id, 'Sólo números')
        return
      }
      if (val <= 0) {
        this.$set(this.payError, id, 'Debe ser mayor a 0')
        return
      }
      if (val > Number(item.balance || 0)) {
        this.$set(this.payError, id, 'No puede exceder el saldo')
        return
      }

      this.$delete(this.payError, id)
    },

    payPartial(item) {
      const id = item.poId
      this.validatePay(item)
      if (this.payError[id]) return

      const amount = Number(this.payDraft[id])
      this.$emit('pay-partial', { poId: id, amount })
      this.$delete(this.payDraft, id)
    },

    remissionLabel(item) {
      const count = Number(item.remissions_count || 0)
      return count > 1 ? `${count} archivos` : 'Completa'
    },

    remissionTitle(item) {
      return item.has_remission
        ? `Remisión cargada (${Number(item.remissions_count || 0)} archivo(s))`
        : 'Remisión pendiente'
    },
  },
}
</script>
